import React from 'react';
import { useSelector, useDispatch } from 'react-redux';
import {
  selectCartItems,
  selectCartTotal,
  selectCartCount,
  removeFromCart,
  updateQuantity,
  selectLanguage
} from '../redux/cartSlice';
import { translations } from '../utils/translations';
import './Cart.css';

const Cart = () => {
  const selectedLang = useSelector(selectLanguage) || { code: 'en' };
  const t = translations[selectedLang.code || 'en'] || translations.en;
  const cartItems = useSelector(selectCartItems);
  const cartTotal = useSelector(selectCartTotal);
  const cartCount = useSelector(selectCartCount);
  const dispatch = useDispatch();

  const [dbShipping, setDbShipping] = React.useState(0);
  const [shippingMethod, setShippingMethod] = React.useState(() => {
    return localStorage.getItem('selected_shipping_method') || 'Shipping on delivery';
  });

  React.useEffect(() => {
    fetch('/api/shipping')
      .then(res => res.json())
      .then(data => {
        if (data && typeof data.amount === 'number') {
          setDbShipping(data.amount);
        }
      })
      .catch(err => console.error('Error fetching shipping charge:', err));
  }, []);

  React.useEffect(() => {
    localStorage.setItem('selected_shipping_method', shippingMethod);
  }, [shippingMethod]);

  // Calculate pricing
  const subtotal = cartTotal;
  const totalQty = cartItems.reduce((acc, item) => acc + item.quantity, 0);
  const isShippingDiscountApplied = totalQty > 2;

  const calculatedShipping = subtotal === 0 ? 0 : cartItems.reduce((acc, item) => {
    const charge = (item.shippingCharges && item.shippingCharges > 0) ? item.shippingCharges : dbShipping;
    return acc + charge;
  }, 0);
  
  const baseShipping = shippingMethod === 'Pickup from store' ? 0 : calculatedShipping;
  const shipping = isShippingDiscountApplied ? baseShipping * 0.5 : baseShipping;

  const vatRate = 0.21; // 21% BTW/VAT typical in NL
  const vatAmount = subtotal * vatRate;
  const total = subtotal + shipping + vatAmount;

  const handleQuantityChange = (id, newQty) => {
    if (newQty < 1) return;
    dispatch(updateQuantity({ id, quantity: newQty }));
  };

  const handleRemoveItem = (id, name) => {
    import('sweetalert2').then((Swal) => {
      const getRemoveText = () => {
        switch (selectedLang.code) {
          case 'nl': return `Weet u zeker dat u ${name} uit de winkelwagen wilt verwijderen?`;
          case 'es': return `¿Estás seguro de que deseas eliminar ${name} del carrito?`;
          case 'fr': return `Êtes-vous sûr de vouloir supprimer ${name} du panier ?`;
          case 'de': return `Sind Sie sicher, dass Sie ${name} aus dem Warenkorb entfernen möchten?`;
          case 'it': return `Sei sicuro di voler rimuovere ${name} dal carrello?`;
          case 'pt': return `Tem certeza de que deseja remover ${name} do carrinho?`;
          case 'el': return `Είστε σίγουροι ότι θέλετε να αφαιρέσετε το προϊόν ${name} από το καλάθι;`;
          default: return `Are you sure you want to remove ${name} from the cart?`;
        }
      };
      const getRemovedText = () => {
        switch (selectedLang.code) {
          case 'nl': return `${name} is verwijderd.`;
          case 'es': return `${name} ha sido eliminado.`;
          case 'fr': return `${name} a été supprimé.`;
          case 'de': return `${name} wurde entfernt.`;
          case 'it': return `${name} è stato rimosso.`;
          case 'pt': return `${name} foi removido.`;
          case 'el': return `Το προϊόν ${name} αφαιρέθηκε.`;
          default: return `${name} has been removed.`;
        }
      };
      Swal.default.fire({
        title: t.removeConfirmTitle,
        text: getRemoveText(),
        icon: 'warning',
        showCancelButton: true,
        confirmButtonColor: '#be1e2d',
        cancelButtonColor: '#718096',
        confirmButtonText: t.yesRemove
      }).then((result) => {
        if (result.isConfirmed) {
          dispatch(removeFromCart(id));
          Swal.default.fire({
            title: t.removedSuccess,
            text: getRemovedText(),
            icon: 'success',
            timer: 1500,
            showConfirmButton: false
          });
        }
      });
    });
  };



  const handleCheckout = () => {
    window.history.pushState({}, '', '/checkout');
    window.dispatchEvent(new PopStateEvent('popstate'));
  };

  const handleContinueShopping = () => {
    window.history.pushState({}, '', '/');
    window.dispatchEvent(new PopStateEvent('popstate'));
  };

  if (cartItems.length === 0) {
    return (
      <div className="cart-empty-wrapper">
        <div className="cart-empty-card">
          <div className="cart-empty-icon">
            <svg width="64" height="64" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
              <path d="M6 2L3 6v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V6l-3-4z" />
              <line x1="3" y1="6" x2="21" y2="6" />
              <path d="M16 10a4 4 0 0 1-8 0" />
            </svg>
          </div>
          <h2 className="cart-empty-title">{t.emptyCartTitle}</h2>
          <p className="cart-empty-subtitle">{t.emptyCartDesc}</p>
          <button className="cart-empty-btn" onClick={handleContinueShopping}>
            {t.continueShopping}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="cart-page-wrapper">
      <div className="cart-page-container">

        {/* Page Header */}
        <div className="cart-header-title-row">
          <h1 className="cart-main-heading">{t.shoppingCart}</h1>
          <span className="cart-header-count">{cartCount} {cartCount === 1 ? t.itemInCart : t.itemsInCart}</span>
        </div>

        {/* 2-Column Responsive Layout */}
        <div className="cart-content-grid">

          {/* Left Column: Cart Items */}
          <div className="cart-items-section">
            <div className="cart-items-list-card">
              {cartItems.map((item) => (
                <div key={item.id} className="cart-item-card">
                  {/* Item Image */}
                  <div className="cart-item-image-box">
                    <img src={item.image} alt={item.name} className="cart-item-img" />
                  </div>

                  {/* Item Info details */}
                  <div className="cart-item-info-box">
                    <span className="cart-item-category">{item.category}</span>
                    <h3 className="cart-item-name" title={item.name}>{item.name}</h3>
                    <div className="cart-item-unit-price">
                      {t.unitPrice}: <span className="price-bold">€{item.price.toFixed(2)}</span>
                      {item.originalPrice && (
                        <span className="price-crossed">€{item.originalPrice.toFixed(2)}</span>
                      )}
                    </div>
                  </div>

                  {/* Quantity adjustment */}
                  <div className="cart-item-qty-box">
                    <div className="cart-qty-selector">
                      <button
                        className="cart-qty-btn minus"
                        onClick={() => handleQuantityChange(item.id, item.quantity - 1)}
                        disabled={item.quantity <= 1}
                      >
                        −
                      </button>
                      <span className="cart-qty-number">{item.quantity}</span>
                      <button
                        className="cart-qty-btn plus"
                        onClick={() => handleQuantityChange(item.id, item.quantity + 1)}
                      >
                        +
                      </button>
                    </div>
                  </div>

                  {/* Item Subtotal Price */}
                  <div className="cart-item-subtotal-box">
                    <span className="item-subtotal-price">€{(item.price * item.quantity).toFixed(2)}</span>
                  </div>

                  {/* Remove Button */}
                  <div className="cart-item-remove-box">
                    <button
                      className="cart-item-remove-btn"
                      onClick={() => handleRemoveItem(item.id, item.name)}
                      aria-label="Remove item"
                    >
                      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <polyline points="3 6 5 6 21 6"></polyline>
                        <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path>
                        <line x1="10" y1="11" x2="10" y2="17"></line>
                        <line x1="14" y1="11" x2="14" y2="17"></line>
                      </svg>
                    </button>
                  </div>
                </div>
              ))}
            </div>

            {/* Back action */}
            <div className="cart-bottom-actions">
              <button className="cart-back-btn" onClick={handleContinueShopping}>
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ marginRight: '8px' }}>
                  <line x1="19" y1="12" x2="5" y2="12"></line>
                  <polyline points="12 19 5 12 12 5"></polyline>
                </svg>
                {t.continueShopping}
              </button>
            </div>
          </div>

          {/* Right Column: Amounts & Summary */}
          <div className="cart-summary-section">
            
            {/* Shipping Method Selector */}
            <div className="shipping-method-section">
              <h3 className="shipping-method-title">Shipping Method</h3>
              <div className="shipping-methods-container">
                
                {/* Pickup from store card */}
                <div 
                  className={`shipping-method-card ${shippingMethod === 'Pickup from store' ? 'selected' : ''}`}
                  onClick={() => setShippingMethod('Pickup from store')}
                >
                  <div className="shipping-card-details">
                    <div className="shipping-card-name">Pickup from store</div>
                    <div className="shipping-card-cost">€0.00</div>
                  </div>
                </div>

                {/* Shipping on delivery card */}
                <div 
                  className={`shipping-method-card ${shippingMethod === 'Shipping on delivery' ? 'selected' : ''}`}
                  onClick={() => setShippingMethod('Shipping on delivery')}
                >
                  <div className="shipping-card-details">
                    <div className="shipping-card-name">Shipping on delivery</div>
                    <div className="shipping-card-cost">
                      {isShippingDiscountApplied ? (
                        <>
                          <span className="shipping-card-cost-crossed">
                            €{calculatedShipping.toFixed(2)}
                          </span>
                          <span className="shipping-card-cost-discounted">
                            +€{(calculatedShipping * 0.5).toFixed(2)}
                          </span>
                        </>
                      ) : (
                        `+€${calculatedShipping.toFixed(2)}`
                      )}
                    </div>
                  </div>
                </div>

              </div>
            </div>

            <div className="cart-summary-card">
              <h2 className="summary-card-title">{t.orderSummary}</h2>

              {/* Price rows */}
              <div className="summary-price-details">
                <div className="summary-row">
                  <span className="summary-row-label">{t.subtotal}</span>
                  <span className="summary-row-val">€{subtotal.toFixed(2)}</span>
                </div>

                 <div className="summary-row" style={{ alignItems: 'flex-start' }}>
                  <span className="summary-row-label">{t.shipping}</span>
                  <span className="summary-row-val" style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end' }}>
                    {shipping === 0 ? (
                      <span className="free-shipping-label">{t.free}</span>
                    ) : isShippingDiscountApplied ? (
                      <>
                        <span style={{ textDecoration: 'line-through', color: '#94a3b8', fontSize: '0.85rem' }}>
                          €{calculatedShipping.toFixed(2)}
                        </span>
                        <span style={{ fontWeight: '600', color: 'var(--nexus-primary, #be1e2d)' }}>
                          €{shipping.toFixed(2)}
                        </span>
                        <span style={{ fontSize: '0.75rem', color: '#16a34a', fontWeight: '600', marginTop: '2px' }}>
                          🔥 50% Multi-item Discount
                        </span>
                      </>
                    ) : (
                      `€${shipping.toFixed(2)}`
                    )}
                  </span>
                </div>



                <div className="summary-row vat-row">
                  <span className="summary-row-label">{t.vatIncluded}</span>
                  <span className="summary-row-val">€{vatAmount.toFixed(2)}</span>
                </div>
                <hr className="summary-divider" />

                <div className="summary-row total-row">
                  <span className="total-label">{t.grandTotal}</span>
                  <span className="total-value">€{total.toFixed(2)}</span>
                </div>
              </div>



              {/* Checkout Button */}
              <button className="cart-checkout-btn" onClick={handleCheckout}>
                {t.proceedToCheckout}
              </button>

              {/* Security & Support info */}
              <div className="summary-security-badges">
                <div className="security-badge-item">
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                    <rect x="3" y="11" width="18" height="11" rx="2" ry="2"></rect>
                    <path d="M7 11V7a5 5 0 0 1 10 0v4"></path>
                  </svg>
                  <span>{t.sslSecureCheckout}</span>
                </div>
                <div className="payment-providers-icons">
                  <img src="/images/payment-icons.webp" alt="iDEAL" className="payment-logo-img" />

                </div>
              </div>
            </div>
          </div>

        </div>
      </div>
    </div>
  );
};

export default Cart;
